import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, Avatar, IconButton, Menu, MenuItem, Divider, Tooltip, Chip } from '@mui/material';
import { LogoutOutlined, KeyboardArrowDown, AccountCircleOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Header = ({ onMenuClick, collapsed, onNavigate }) => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    try {
      await (await import('../api/auth')).logout();
    } catch {}
    setUser(null);
    navigate('/login');
  };

  const displayName = user?.fullName || user?.email || 'User';
  const displayEmail = user?.email || '';
  const displayInitial = displayName.charAt(0).toUpperCase();

  const getRoleLabel = () => {
    if (user?.role === 'admin') return 'Admin';
    if (user?.role === 'reseller') return 'Reseller';
    return 'Client';
  };

  return (
    <AppBar
      position="static"
      sx={{
        height: 64,
        background: 'linear-gradient(135deg, #ffffff 0%, #f0faf5 50%, #f5fffa 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradientShift 8s ease-in-out infinite',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        borderBottom: '1px solid rgba(229,231,235,0.6)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', height: 64, px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box component="img" src="/talktokam-green-wa.png" alt="Talktokam"
            sx={{ height: 36, width: 'auto', borderRadius: '6px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
          <Typography variant="body2" fontWeight={600} color="#075e54" sx={{ display: { xs: 'none', sm: 'block' } }}>
            WhatsApp API
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip label={getRoleLabel()} size="small"
            sx={{ bgcolor: 'rgba(7,94,84,0.1)', color: '#075e54', fontWeight: 700, fontSize: 11, borderRadius: '6px' }} />

          {user && (
            <>
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                  px: 1.5, py: 0.8, borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'rgba(7,94,84,0.06)' },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#075e54', width: 34, height: 34, fontWeight: 700, fontSize: 14,
                    background: 'linear-gradient(135deg, #075e54, #0a8a7a)',
                  }}
                >
                  {displayInitial}
                </Avatar>
                <Box sx={{ lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
                  <Typography fontWeight={600} fontSize="13px" color="#1f2937" sx={{ lineHeight: 1.3 }}>
                    {displayName}
                  </Typography>
                  <Typography fontSize="11px" color="#9CA3AF" sx={{ lineHeight: 1.3, mt: 0.2 }}>
                    {displayEmail}
                  </Typography>
                </Box>
                <KeyboardArrowDown sx={{ color: '#9CA3AF', fontSize: 18 }} />
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1, borderRadius: 2.5,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(229,231,235,0.5)',
                    minWidth: 220,
                    '&::before': {
                      content: '""', position: 'absolute', top: -6, right: 20,
                      width: 12, height: 12, bgcolor: '#fff',
                      transform: 'rotate(45deg)',
                      borderLeft: '1px solid rgba(229,231,235,0.5)',
                      borderTop: '1px solid rgba(229,231,235,0.5)',
                    },
                  },
                }}
              >
                <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: '#075e54', width: 40, height: 40, fontWeight: 700, fontSize: 16 }}>
                    {displayInitial}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize="14px" color="#1f2937">{displayName}</Typography>
                    <Typography fontSize="12px" color="#9CA3AF">{displayEmail}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ mx: 1.5 }} />
                <MenuItem onClick={() => { setAnchorEl(null); if (onNavigate) onNavigate('settings'); }}
                  sx={{ mx: 1, borderRadius: 1.5, py: 0.8, gap: 1.5, fontSize: 13, color: '#374151' }}>
                  <AccountCircleOutlined sx={{ fontSize: 18, color: '#9CA3AF' }} />
                  Account Settings
                </MenuItem>
                <Divider sx={{ mx: 1.5 }} />
                <MenuItem onClick={handleLogout}
                  sx={{ mx: 1, borderRadius: 1.5, py: 0.8, gap: 1.5, color: '#EF4444', fontSize: 13, fontWeight: 500 }}>
                  <LogoutOutlined sx={{ fontSize: 18 }} />
                  Logout
                </MenuItem>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography fontSize={11} color="#D1D5DB" textAlign="center">Talktokam WhatsApp v1.0</Typography>
                </Box>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
